import { useEffect, useState } from 'react'

/* =========================================================================
   Current user
   -------------------------------------------------------------------------
   localStorage holds exactly one thing: the session id. Every other detail
   (name, email, phone, role, avatar) is fetched from /api/get-users.php and
   kept in memory, so a stale copy can never be trusted or tampered with.
   ========================================================================= */

const API_BASE = import.meta.env.VITE_API_BASE || '/api'
const SESSION_KEY = 'session_id'

/* Written by older builds — cleared once so nothing keeps reading them. */
const LEGACY_KEYS = ['user_name', 'user_email', 'user_phone', 'user_role', 'user_avatar', 'user_id']

let cachedUser = null
let inflight = null
const listeners = new Set()

function purgeLegacyKeys() {
  if (typeof localStorage === 'undefined') return
  for (const key of LEGACY_KEYS) {
    if (localStorage.getItem(key) !== null) localStorage.removeItem(key)
  }
}
purgeLegacyKeys()

function emit() {
  for (const fn of listeners) fn(cachedUser)
}

export function getSessionId() {
  if (typeof localStorage === 'undefined') return ''
  return localStorage.getItem(SESSION_KEY) || ''
}

/* Roles the phone wall does not apply to.

   The wall is there to stop a throwaway Google address becoming a client
   account nobody can ring. Staff accounts are not created that way, and
   walling them would lock the team out of their own admin — permanently, if
   phone sign-in is not switched on in Firebase yet. */
const STAFF_ROLES = new Set(['super_admin', 'admin', 'staff', 'team'])

/**
 * Whether a signed-in account still has to give us a number.
 *
 * Exported rather than written out at each call site because the gate and the
 * client tab bar have to agree exactly: if the bar shows while the gate is up,
 * it offers one-tap links into a workspace that will not open.
 *
 * Keyed on `phone` alone. It is the one field every shape of the user object
 * carries — /api/login.php and /api/get-users.php return different sets, and
 * the second overwrites the first on the next refresh.
 */
export function needsPhone(user) {
  if (!user) return false
  if (STAFF_ROLES.has(String(user.role || '').trim())) return false

  /* Verified, not merely present. A number that was typed and never confirmed
     is exactly the number a throwaway account supplies, so treating it as
     good would leave the wall standing next to an open door.

     contact_verified_at is absent from the shape /api/login.php returns, so a
     missing field falls back to "is there a number at all" — otherwise the
     gate would flash on every fresh login before the first refresh replaces
     that object with the fuller one from /api/get-users.php. */
  if (!('contact_verified_at' in user)) return !String(user.phone || '').trim()

  return !String(user.contact_verified_at || '').trim()
}

export function getCachedUser() {
  return cachedUser
}

/** Seed the cache from a response the caller already has (login, profile save). */
export function setCurrentUser(user) {
  cachedUser = user || null
  inflight = null
  emit()
}

/** Drop the session and everything derived from it. */
export function clearCurrentUser() {
  cachedUser = null
  inflight = null
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(SESSION_KEY)
    purgeLegacyKeys()
  }
  emit()
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('storage'))
}

/**
 * Resolve the signed-in user from the session id. Concurrent callers share a
 * single request; an invalid session resolves to null rather than throwing.
 */
export function fetchCurrentUser({ force = false } = {}) {
  const sessionId = getSessionId()
  if (!sessionId) {
    cachedUser = null
    return Promise.resolve(null)
  }
  if (cachedUser && !force) return Promise.resolve(cachedUser)
  if (inflight && !force) return inflight

  inflight = fetch(`${API_BASE}/get-users.php?session_id=${encodeURIComponent(sessionId)}`, {
    headers: { Accept: 'application/json' },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      cachedUser = data && data.success && data.user ? data.user : null
      inflight = null
      emit()
      return cachedUser
    })
    .catch(() => {
      inflight = null
      return cachedUser
    })

  return inflight
}

export function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Components read the user through this; it refetches when the session changes. */
export function useCurrentUser() {
  const [user, setUser] = useState(cachedUser)
  const [loading, setLoading] = useState(Boolean(getSessionId()) && !cachedUser)

  useEffect(() => {
    let cancelled = false
    const unsubscribe = subscribe((u) => { if (!cancelled) setUser(u) })

    const sync = () => {
      if (!getSessionId()) {
        setCurrentUser(null)
        setLoading(false)
        return
      }
      setLoading(!getCachedUser())
      fetchCurrentUser().then(() => { if (!cancelled) setLoading(false) })
    }

    sync()
    window.addEventListener('storage', sync)
    window.addEventListener('focus', sync)
    return () => {
      cancelled = true
      unsubscribe()
      window.removeEventListener('storage', sync)
      window.removeEventListener('focus', sync)
    }
  }, [])

  return { user, loading, isLoggedIn: Boolean(user) }
}
